import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Download, ExternalLink, FileText, FileDown, Globe2, History, Loader2, Twitter } from "lucide-react";

/**
 * Workspace-wide feed of recent publish events across all of the user's
 * projects + drafts. Powers the dashboard "Latest publishes" panel.
 */
const KIND_META = {
  wordpress: { label: "WordPress", Icon: Globe2, tone: "border-blue-200 bg-blue-50 text-blue-700" },
  arloseo_blog: { label: "ArloSEO blog", Icon: FileText, tone: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  notion:    { label: "Notion / clipboard", Icon: FileText, tone: "border-slate-200 bg-slate-50 text-slate-700" },
  twitter:   { label: "X / Twitter", Icon: Twitter, tone: "border-sky-200 bg-sky-50 text-sky-700" },
  markdown:  { label: "Markdown export", Icon: FileDown, tone: "border-amber-200 bg-amber-50 text-amber-700" },
};

const KIND_TABS = [
  { id: "all", label: "All" },
  { id: "wordpress", label: "WordPress" },
  { id: "arloseo_blog", label: "Blog" },
  { id: "notion", label: "Notion" },
  { id: "twitter", label: "Social" },
  { id: "markdown", label: "Markdown" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function LatestPublishes({ limit = 25 }) {
  const [events, setEvents] = useState(null); // null = loading, [] = empty
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/publish/recent?limit=${limit}`);
        if (!cancelled) setEvents(data.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  const counts = useMemo(() => {
    const out = { all: events?.length || 0 };
    for (const e of events || []) {
      const k = e.kind || "other";
      out[k] = (out[k] || 0) + 1;
    }
    return out;
  }, [events]);

  const visible = useMemo(() => {
    if (!events) return [];
    if (filter === "all") return events;
    return events.filter((e) => e.kind === filter);
  }, [events, filter]);

  const downloadCsv = async () => {
    if (!events || events.length === 0) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ days: "30" });
      if (filter !== "all") params.set("kind", filter);
      const res = await api.get(`/publish/recent.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      const suffix = filter === "all" ? "" : `-${filter}`;
      a.href = url;
      a.download = `arloseo-publishes-30d${suffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // non-fatal
    }
    setExporting(false);
  };

  return (
    <section className="border border-border bg-white" data-testid="latest-publishes">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4" />
          <h3 className="font-display tracking-tight text-base">Latest publishes</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1" data-testid="latest-publishes-filters">
            {KIND_TABS.map((t) => {
              const active = filter === t.id;
              const count = counts[t.id] || 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFilter(t.id)}
                  disabled={t.id !== "all" && count === 0}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono-data text-[10px] uppercase tracking-widest transition-colors ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground hover:border-foreground hover:text-foreground disabled:opacity-40 disabled:hover:border-border disabled:hover:text-muted-foreground"
                  }`}
                  data-testid={`latest-publishes-filter-${t.id}`}
                >
                  {t.label} <span className={active ? "opacity-70" : ""}>· {count}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={exporting || !events || events.length === 0}
            className="inline-flex items-center gap-1 px-2 py-0.5 border border-border font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground hover:border-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:hover:border-border disabled:hover:text-muted-foreground"
            title={filter === "all" ? "Export the last 30 days" : `Export the last 30 days of ${filter} events`}
            data-testid="latest-publishes-export-csv"
          >
            {exporting
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Download className="w-3 h-3" />} CSV · 30d
          </button>
        </div>
      </div>

      {events === null && (
        <div className="px-5 py-6 flex items-center gap-2 text-xs text-muted-foreground" data-testid="latest-publishes-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      )}

      {events && events.length === 0 && (
        <div className="px-5 py-8 text-center" data-testid="latest-publishes-empty">
          <div className="w-12 h-12 mx-auto bg-foreground grid place-items-center text-background mb-3">
            <FileText className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <p className="text-sm text-muted-foreground">
            Nothing published yet. Open a project&apos;s <strong>Content Studio</strong> → Drafts → <em>Publish</em>.
          </p>
        </div>
      )}

      {events && events.length > 0 && visible.length === 0 && (
        <div className="px-5 py-6" data-testid="latest-publishes-filter-empty">
          <p className="text-sm text-muted-foreground">
            No <strong>{KIND_TABS.find((t) => t.id === filter)?.label}</strong> events yet.{" "}
            <button onClick={() => setFilter("all")} className="underline underline-offset-4 text-foreground" data-testid="latest-publishes-filter-empty-reset">
              show all
            </button>
          </p>
        </div>
      )}

      {visible.length > 0 && (
        <ul className="divide-y divide-border" data-testid="latest-publishes-list">
          {visible.map((e, i) => {
            const meta = KIND_META[e.kind] || { label: e.kind, Icon: ExternalLink, tone: "border-border bg-white text-muted-foreground" };
            const Icon = meta.Icon;
            return (
              <li key={`${e.draft_id}-${e.at}-${i}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-secondary/40 transition-colors" data-testid={`latest-publishes-item-${i}`}>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-data text-[10px] uppercase tracking-widest shrink-0 ${meta.tone}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                <Link
                  to={`/app/projects/${e.project_id}`}
                  className="text-sm font-medium truncate hover:underline underline-offset-4"
                  title={e.draft_title}
                  data-testid={`latest-publishes-draft-${i}`}
                >
                  {e.draft_title || "Untitled"}
                </Link>
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground truncate hidden sm:inline">
                  · {e.project_name || e.project_domain || "—"}
                </span>
                {e.status && (
                  <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    {e.status}
                  </span>
                )}
                <span className="ml-auto font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
                  {timeAgo(e.at)}
                </span>
                {e.url && (
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 text-xs underline underline-offset-4 shrink-0"
                    data-testid={`latest-publishes-link-${i}`}
                  >
                    open <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
